import {AttributesRow} from 'platform/components';
import {Grid, Hide, Text} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetRentalPriceGroupQuery, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getPriceWithVat, useTenant, useVatRatesOptions} from '@dms/shared';

import {Nullish} from 'shared/web';

export const useGetRentalVehicleBasicInformation = (
  rentalVehicleId: string | Nullish
): [AttributesRow[], boolean] => {
  const formatCurrency = useFormatCurrency();
  const {tenantCurrency} = useTenant();
  const [vatRates] = useVatRatesOptions();

  const {data: rentalVehicle, isLoading: isRentalVehicleLoading} = useGetRentalVehicleQuery(
    {vehicleId: rentalVehicleId ?? ''},
    {skip: isNil(rentalVehicleId)}
  );
  const priceGroupId = rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId;
  const {data: priceGroup, isLoading: isPriceGroupLoading} = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: priceGroupId ?? ''},
    {skip: isNil(priceGroupId)}
  );

  const priceGroupItem = priceGroup?.items?.find(
    (item) =>
      item.rentalPriceGroupItemId === rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId
  );

  const priceGroupVatRate = vatRates.find((vatRate) => vatRate.type === priceGroup?.vatType)?.rate;

  const formatRate = (value: string | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    return formatCurrency(parseFloat(value), tenantCurrency, 2);
  };

  const formatRateWithVat = (priceWithoutVat: string | Nullish) => {
    const vatRate = Number(priceGroupVatRate);

    if (isNil(priceWithoutVat) || isNilOrEmpty(vatRate) || isNaN(vatRate)) {
      return null;
    }

    const priceWithVat = getPriceWithVat(priceWithoutVat, vatRate);
    return formatCurrency(priceWithVat, tenantCurrency, 2);
  };

  const rateWithVat = formatRateWithVat(priceGroupItem?.price?.rate);
  const ratePerKilometerWithVat = formatRateWithVat(priceGroupItem?.price?.ratePerKilometer);

  const isLoading = isRentalVehicleLoading || isPriceGroupLoading;

  return [
    [
      {
        label: i18n.t('page.rentalOrder.labels.dailyRate'),
        content: (
          <Grid columns={2}>
            <Text size="xSmall" color="secondary">
              {formatRate(priceGroupItem?.price?.rate)}
            </Text>

            <Hide when={isNil(rateWithVat)}>
              <Text size="xSmall" color="secondary">
                {`${rateWithVat} ${i18n.t('general.labels.withVat')}`}
              </Text>
            </Hide>
          </Grid>
        ),
        testId: testIds.rentalOrder.vehicleAndCustomer('dailyRate'),
      },
      {
        label: i18n.t('page.rentalOrder.labels.ratePerKilometer'),
        content: (
          <Grid columns={2}>
            <Text size="xSmall" color="secondary">
              {formatRate(priceGroupItem?.price?.ratePerKilometer)}
            </Text>

            <Hide when={isNil(ratePerKilometerWithVat)}>
              <Text size="xSmall" color="secondary">
                {`${ratePerKilometerWithVat} ${i18n.t('general.labels.withVat')}`}
              </Text>
            </Hide>
          </Grid>
        ),
        testId: testIds.rentalOrder.vehicleAndCustomer('ratePerKilometer'),
      },
    ],
    isLoading,
  ] as const;
};
